import React, { useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Card,
  Menu,
  MenuItem,
  Stack,
  Chip,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router";

import { PROJECTS } from "src/platform/constants/routes";
import { getCardStyles } from "src/shell/styles/themeUtils";

import type { Organization } from "../../types";

interface OrganizationHeaderProps {
  organization: Organization;
  onEditOrganization?: () => void;
  onInviteMember?: () => void;
  onNavigateBilling?: () => void;
}

function statusLabel(status: Organization["status"]): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

const OrganizationHeader: React.FC<OrganizationHeaderProps> = ({
  organization,
  onEditOrganization,
  onInviteMember,
  onNavigateBilling,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Card
      sx={{
        ...getCardStyles(theme),
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          flexWrap="wrap"
        >
          <Typography variant="h4" component="h1" fontWeight={700}>
            {organization.name}
          </Typography>
          <Chip
            label={statusLabel(organization.status)}
            size="small"
            color={organization.status === "active" ? "success" : "default"}
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            component={RouterLink}
            to={PROJECTS.LIST.getPath(organization.alias)}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            View projects
          </Button>
          <Button
            variant="outlined"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            endIcon={<MoreVertIcon fontSize="small" />}
            aria-haspopup="menu"
            aria-expanded={menuOpen ? "true" : undefined}
            sx={{ textTransform: "none" }}
          >
            Manage organization
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={menuOpen}
            onClose={closeMenu}
            MenuListProps={{ "aria-label": "Manage organization actions" }}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                onInviteMember?.();
              }}
            >
              Invite member
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onNavigateBilling?.();
              }}
            >
              Billing
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onEditOrganization?.();
              }}
            >
              Settings
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {organization.description && (
        <Box sx={{ mt: 1.5, maxWidth: 760 }}>
          <Typography color="text.secondary">
            {organization.description}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default OrganizationHeader;
